import { useCallback, useEffect, useState } from 'react';
import Page from '../../../components/page/Page';
import Header from '../../../components/operations/receptionOrders/Header';
import { IconButton, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useGlobalCore } from '../../../../core/modules/global';
import CreateDialog from '../../../components/operations/receptionOrders/CreateDialog';
import UpdateDialog from '../../../components/operations/receptionOrders/UpdateDialog';
import { useCompanyCore } from '../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { UPDATE_RECEPTION_ORDER_DIALOG } from '../../../../core/constants/dialog';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useBusinessCore } from '../../../../core/modules/business';
import moment from 'moment';
import { useMasterCore } from '../../../../core/modules/master';
import { DOCUMENT_TYPE_LABEL } from '../../../../core/constants/documentTypes';
import { ReceptionOrder } from '../../../../core/interfaces/receptionOrder.interface';
import Swal from 'sweetalert2';

const ReceptionOrders = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { receptionOrders, setReceptionOrder, getReceptionOrders, deleteReceptionOrder } = useReceptionCore();
  const { goToWelcome } = useNavigationCore();
  const { getBusinesses } = useBusinessCore();
  const { getTypes } = useMasterCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.readReception &&
        !user.privileges.writeReception
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  const fetchReceptionOrders = useCallback(() => {
    if (!companyId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getReceptionOrders(
      companyId,
      undefined,
      () => setIsLoading(false),
      () => setIsLoading(false)
    );
  }, [companyId, getReceptionOrders]);

  useEffect(() => {
    fetchReceptionOrders();
  }, [fetchReceptionOrders]);

  // Manejar el error de ResizeObserver
  useEffect(() => {
    const handleResizeObserverError = () => {
      // Silenciar el error de ResizeObserver
      const originalError = console.error;
      console.error = (...args) => {
        if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
          return;
        }
        originalError.apply(console, args);
      };
    };

    handleResizeObserverError();
  }, []);

  const selectReception = useCallback(async (reception: ReceptionOrder) => {
    setReceptionOrder(reception);
    if (companyId) {
      // Cargar los datos necesarios antes de abrir el diálogo
      await Promise.all([
        getTypes(companyId, 'acquisition'),
        getBusinesses(companyId)
      ]);
    }
    openDialog(UPDATE_RECEPTION_ORDER_DIALOG);
  }, [setReceptionOrder, companyId, openDialog, getTypes, getBusinesses]);

  const deleteReceptionOrderFromTable = useCallback((reception: ReceptionOrder) => {
    Swal.fire({
      title: "Confirmar acción",
      text: `¿Eliminar la orden de ingreso ${reception.document_number}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId) {
        deleteReceptionOrder(
          companyId,
          reception.id,
          () => {
            fetchReceptionOrders();
            Swal.fire({
              title: "Eliminado!",
              text: "La orden de ingreso ha sido eliminada.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
          },
          () => {
            Swal.fire({
              title: "Error!",
              text: "No se pudo eliminar la orden de ingreso.",
              icon: "error"
            });
          }
        );
      }
    });
  }, [companyId, deleteReceptionOrder, fetchReceptionOrders]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      cellClassName: 'flex items-center p-8',
      flex: 1,
    },
    {
      field: 'reception_date',
      headerName: 'Fecha de Recepción',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{moment(params.row.reception_date*1000).format('DD/MM/YYYY')}</div>
      ),
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.description || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'document_type',
      headerName: 'Tipo de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{DOCUMENT_TYPE_LABEL[params.row.document_type_id]}</div>
      ),
      flex: 1,
    },
    {
      field: 'document_number',
      headerName: 'Número de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.document_number || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'cost',
      headerName: 'Costo',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.cost && params.row.cost > 0 ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(params.row.cost) : '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'commission_percentage',
      headerName: 'Comisión (%)',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <p>{params.row.commission && params.row.commission > 0 ? `${params.row.commission}%` : '-'}</p>
      ),
      flex: 1,
    },
    {
      field: 'has_receptions',
      headerName: 'Estado',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          params.row.has_receptions 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {params.row.has_receptions ? 'Con recepciones' : 'Sin recepciones'}
        </div>
      ),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div className="flex gap-2">
          {!params.row.has_receptions && (
            <IconButton
              aria-label="delete"
              onClick={() => deleteReceptionOrderFromTable(params.row)}
              size="small"
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="edit"
            onClick={() => selectReception(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                <div 
                  style={{ 
                    height: '600px', 
                    width: '100%', 
                    position: 'relative'
                  }}
                >
                  <DataGrid
                    rows={receptionOrders}
                    columns={columns}
                    sx={{ 
                      border: 0,
                      '& .MuiDataGrid-virtualScroller': { 
                        overflowY: 'scroll !important'
                      }
                    }}
                    disableColumnSorting
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    getRowHeight={() => 'auto'}
                    pagination={undefined}
                    loading={isLoading}
                  />
                </div>
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UpdateDialog />
    </>
  );
};

export default ReceptionOrders;
